import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { BreadcrumbNavigation } from '../components/BreadcrumbNavigation';
import { SeoHead } from '../components/SeoHead';

/**
 * ClusterPage
 * Authoritative static content pages for Topic Clusters (e.g., SSC CGL Salary, Syllabus).
 */
export function ClusterPage({ darkMode }) {
  const location = useLocation();
  const path = location.pathname.toLowerCase().replace('/', '');

  // Define configuration mapping for clusters
  const clusters = useMemo(() => {
    const sscLinks = [
      { path: 'ssc-cgl-salary', label: 'SSC CGL Salary' },
      { path: 'ssc-cgl-syllabus', label: 'SSC CGL Syllabus' },
      { path: 'ssc-cgl-exam-pattern', label: 'SSC CGL Exam Pattern' },
      { path: 'ssc-cgl-cutoff', label: 'SSC CGL Cutoff' },
      { path: 'ssc-cgl-books', label: 'Best Books for SSC CGL' }
    ];
    
    const ntpcLinks = [
      { path: 'railway-ntpc-salary', label: 'Railway NTPC Salary' },
      { path: 'railway-ntpc-syllabus', label: 'Railway NTPC Syllabus' },
      { path: 'railway-ntpc-cutoff', label: 'Railway NTPC Cutoff' }
    ];

    return {
      'ssc-cgl-salary': { title: 'SSC CGL Salary 2026: In-Hand, Pay Scale & Allowances', parent: 'SSC CGL', clusterLinks: sscLinks },
      'ssc-cgl-syllabus': { title: 'SSC CGL Syllabus 2026: Tier 1 & Tier 2 Detailed Syllabus', parent: 'SSC CGL', clusterLinks: sscLinks },
      'ssc-cgl-exam-pattern': { title: 'SSC CGL Exam Pattern 2026', parent: 'SSC CGL', clusterLinks: sscLinks },
      'ssc-cgl-cutoff': { title: 'SSC CGL Cutoff Marks (Previous Years)', parent: 'SSC CGL', clusterLinks: sscLinks },
      'ssc-cgl-books': { title: 'Best Books for SSC CGL Preparation', parent: 'SSC CGL', clusterLinks: sscLinks },
      
      'railway-ntpc-salary': { title: 'Railway NTPC Salary 2026: Pay Scale & Allowances', parent: 'Railway NTPC', clusterLinks: ntpcLinks },
      'railway-ntpc-syllabus': { title: 'Railway NTPC Syllabus 2026: CBT 1 & CBT 2', parent: 'Railway NTPC', clusterLinks: ntpcLinks },
      'railway-ntpc-cutoff': { title: 'Railway NTPC Cutoff Marks (Zone-wise)', parent: 'Railway NTPC', clusterLinks: ntpcLinks },
    };
  }, []);

  const currentConfig = clusters[path] || { title: 'Topic Details', parent: 'Category', clusterLinks: [] };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SeoHead 
        title={`${currentConfig.title} | Sarkari Result`} 
        description={`Comprehensive details about ${currentConfig.title}. Read the full guide here.`}
        canonical={`https://www.sarkariresult.cloud/${path}`}
      />
      
      <div className="max-w-6xl mx-auto px-3 py-4 sm:p-6 flex flex-col md:flex-row gap-6">
        
        {/* Main Content Area */}
        <div className="flex-grow max-w-4xl">
          <BreadcrumbNavigation paths={[
            { label: 'Home', link: '/' },
            { label: currentConfig.parent, link: `/${currentConfig.parent.toLowerCase().replace(' ', '-')}-jobs` },
            { label: currentConfig.title, link: `/${path}` }
          ]} darkMode={darkMode} />
          
          <div className={`p-6 sm:p-8 rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h1 className={`text-2xl sm:text-3xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentConfig.title}
            </h1>
            
            <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
              <p>Welcome to the comprehensive guide on <strong>{currentConfig.title}</strong>.</p>
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
              
              <h2>Important Information</h2>
              <p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
              <ul>
                <li>Point 1: Lorem ipsum dolor sit amet.</li>
                <li>Point 2: Consectetur adipiscing elit.</li>
                <li>Point 3: Sed do eiusmod tempor incididunt.</li>
              </ul>

              <h2>Frequently Asked Questions</h2>
              <p><strong>Q: Is this information updated?</strong><br/>A: Yes, this information is updated for the 2026 cycle.</p>
              
              <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 rounded">
                <p className="text-sm m-0 text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> This is a highly optimized placeholder cluster page. The admin can replace this generic lorem ipsum content with detailed, specific information regarding Salary, Syllabus, and Cutoffs.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar for Internal Linking (Topic Cluster) */}
        <div className="w-full md:w-64 flex-shrink-0">
          <div className={`sticky top-20 p-5 rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 pb-2 border-b ${darkMode ? 'text-gray-200 border-gray-700' : 'text-gray-800 border-gray-200'}`}>
              Related Topics
            </h3>
            <ul className="space-y-3">
              {currentConfig.clusterLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={`/${link.path}`}
                    className={`block text-sm hover:underline ${path === link.path ? (darkMode ? 'text-sarkari-red font-bold' : 'text-sarkari-red font-bold') : (darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-black')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}
